/**
 * Console money helpers — exact bigint cents only (audit RC-6).
 *
 * Arithmetic and parsing are delegated to the shared financials-component
 * exact-decimal implementation. JavaScript `number` must never carry money.
 */

import {
  parseDecimal,
} from "../../../financials-component/src/decimal.js";

export type UsdCents = bigint;

/** Account types that count as cash (Plaid). Credit/loan/investment are not cash. */
export const CASH_ACCOUNT_TYPES: ReadonlySet<string> = new Set(["depository"]);

/** Parse a contract decimal string to exact cents. Returns null on bad input. */
export function decimalToCents(amount: string): UsdCents | null {
  try {
    return parseDecimal(amount);
  } catch {
    return null;
  }
}

function groupThousands(digits: string): string {
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

/** Format exact cents for console display: "$1,234.56" / "−$1,234.56". */
export function formatCents(cents: UsdCents): string {
  const negative = cents < 0n;
  const abs = negative ? -cents : cents;
  const whole = abs / 100n;
  const frac = (abs % 100n).toString().padStart(2, "0");
  const formatted = `${groupThousands(whole.toString())}.${frac}`;
  return negative ? `−$${formatted}` : `$${formatted}`;
}

/**
 * Format a contract decimal amount for display with thousands separators and
 * the one console minus style. A value that is not an exact amount is returned
 * unchanged so a caller never prints "NaN" or silently zeroes a number.
 */
export function formatDecimalMoney(amount: string): string {
  const cents = decimalToCents(amount);
  return cents === null ? amount : formatCents(cents);
}

/**
 * An amount stored MONEY-OUT-POSITIVE in the console's money-in-positive sign
 * (rd L2 review round 1). Two stored populations are money-out-positive: the
 * hub's own receipts and bills (`books-api.yaml` `AdminHubIntakeItem.amount`:
 * "a purchase or payment is positive") and the bank feed's lines (Plaid's sign,
 * the read `plaidAmountToAccounting` makes). One helper, so a receipt and the
 * bank line it matches can never read opposite ways — and so the same receipt
 * cannot show one sign on the shelf and another in the hub list.
 *
 * `fallback` is returned for a value that is not an exact amount, never a blank
 * or a raw string.
 */
export function formatOutPositiveMoney(amount: string, fallback = ""): string {
  const cents = decimalToCents(amount);
  return cents === null ? fallback : formatCents(-cents);
}

/** `asOf` is typically YYYY-MM-DD → "June 30". */
export function formatAsOf(isoDate: string): string {
  const d = new Date(`${isoDate}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return isoDate;
  return d.toLocaleDateString(undefined, {
    month: "long",
    day: "numeric",
    timeZone: "UTC",
  });
}

export interface CashBalanceAccount {
  currentBalance: string;
  currency: string;
  type: string;
}

/**
 * Sum USD currentBalances across **cash** (depository) accounts only.
 * Credit-card debt and other non-cash types are excluded (C-10).
 *
 * Returns null when empty input, mixed/non-USD currency among cash accounts,
 * or any malformed amount — callers must treat null as incomplete, never as 0.
 *
 * When the snapshot has accounts but none are cash, returns 0n (legitimate
 * zero cash, not unknown).
 */
export function sumCashUsdCents(
  accounts: ReadonlyArray<CashBalanceAccount>,
): UsdCents | null {
  if (accounts.length === 0) return null;
  const cashAccounts = accounts.filter((a) => CASH_ACCOUNT_TYPES.has(a.type));
  if (cashAccounts.length === 0) return 0n;
  const currencies = new Set(cashAccounts.map((a) => a.currency));
  if (currencies.size !== 1 || !currencies.has("USD")) return null;
  let cents = 0n;
  for (const a of cashAccounts) {
    const parsed = decimalToCents(a.currentBalance);
    if (parsed === null) return null;
    cents += parsed;
  }
  return cents;
}

/**
 * Exact sum of already-parsed USD cents. Returns null when `values` is empty
 * (no entitled total). Never coerces through `number`.
 */
export function sumUsdCentsExact(values: readonly UsdCents[]): UsdCents | null {
  if (values.length === 0) return null;
  let total = 0n;
  for (const v of values) total += v;
  return total;
}
